import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {DasUBoot} from "../DasUBoot";
import {DasUBootGameMode} from "../server/DasUBootGameMode";
import {DasUBootSymbol, DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {gsap} from "gsap";
import {SymbolUtils} from "../utils/SymbolUtils";
import {GSAPUtils} from "../utils/GSAPUtils";
import {isBombSymbol} from "../reels/symbols/BombSymbol";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {MathUtils} from "../utils/MathUtils";
import {StateMatrixCoordinate} from "../utils/StateMatrix";
import {DasUBootSound} from "../sound/DasUBootSound";
import {DasUBootStatics} from "../utils/DasUBootStatics";

/**
 * Created by jonas on 2021-02-19.
 */

export class xBomb implements SimplePresentation {

    public calculateClosesDistanceToABomb(symbolData:SimpleReelModelSymbolData, bombs:SimpleReelModelSymbolData[]) {

        let closestDistance = Number.MAX_VALUE;
        for (let bomb of bombs) {
            let yPos = symbolData.reelPosition;
            yPos = symbolData.reelId == 0 || symbolData.reelId == 5 ? yPos + 1.5 : yPos;
            yPos = symbolData.reelId == 1 || symbolData.reelId == 4 ? yPos + 1.0 : yPos;
            closestDistance = Math.min(
                closestDistance,
                MathUtils.distanceBetweenPoints(new PIXI.Point(bomb.reelId, bomb.reelPosition), new PIXI.Point(symbolData.reelId, yPos))
            );
        }

        return closestDistance;
    }


    start(controller:SimplePresentationRunnerController):void {

        //DATA
        const explodePositions:StateMatrixCoordinate[] = DasUBoot.model.xBombCollapseMatrix.getAllCoordinateForValue(-1);
        const symbolsToReset:SimpleReelModelSymbolData[] = DasUBoot.reelView.getVisibleSymbolsWhere((value:SimpleReelModelSymbolData) => {
            return !isBombSymbol(value.positionable);
        });

        const bombSymbols:SimpleReelModelSymbolData[] = DasUBoot.reelView.getVisibleSymbolsWhere((value:SimpleReelModelSymbolData) => {
            return isBombSymbol(value.positionable);
        });


        //Main TL
        const tl:gsap.core.Timeline = gsap.timeline({onComplete:() => controller.finish()});
        tl.add(SymbolUtils.getSymbolStateAnimations(symbolsToReset, DasUBootSymbolStates.NORMAL));

        //tl.add(DasUBoot.reelView.reelFrame.xBombMultiplierDisplay.incrementMultiplier(0.6, (<DasUBootSymbol>bombSymbols[0].positionable).getGlobalPosition(true)));
        tl.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.BOMB_EXPLODE_SOUND));
        tl.add(() => {DasUBoot.reelView.bombShake()});

        tl.add(SymbolUtils.getSymbolStateAnimations(bombSymbols, DasUBootSymbolStates.EXPLODE));
        tl.add(this.getExplosions(explodePositions, bombSymbols), "<0.0");

        const fallDownTime = tl.duration();

        const resizeData = this.getResizeAnimation();
        tl.add(resizeData.anim, fallDownTime); //Needs to happen before fall down. This resets the scale before falling animation is calculated
        tl.add(this.getFallDownAnimation(resizeData.symbols), fallDownTime);

        tl.add(DasUBoot.reelView.reelFrame.avalancheAttention.show(0.001), 0);


    }

    private getExplosions(explodePositions:StateMatrixCoordinate[], bombSymbols:SimpleReelModelSymbolData[]) {
        //Explosions
        const explodingSymbols:SimpleReelModelSymbolData[] = [];
        for (let pos of explodePositions) {
            const symbolData:SimpleReelModelSymbolData = DasUBoot.reelView.getVisibleSymbolAt(pos.column, pos.row);
            explodingSymbols.push(symbolData);
        }

        const explosions = gsap.timeline();
        for (let explodingSymbol of explodingSymbols) {
            const smallestDistance = this.calculateClosesDistanceToABomb(explodingSymbol, bombSymbols);

            explosions.add([
                // () => DasUBoot.soundPlayer.playSound(DasUBootSound.symbolExplodeSounds.next(), 0.5),
                (<DasUBootSymbol>explodingSymbol.positionable).getStateAnimation(DasUBootSymbolStates.EXPLODE)
            ], 0.08 * smallestDistance);

            if (explodingSymbol.name == "M") {
                explosions.add(() => DasUBoot.reelView.reelFrame.switchLampOnReel(explodingSymbol.reelId, -1), "<");
            }
        }

        explosions.add(() => SymbolUtils.removeExplodedSymbols(explodePositions));
        return explosions;
    }


    private getFallDownAnimation(resizedSymbol:SimpleReelModelSymbolData[]) {
        //collapse remaining symbols
        const fallDownPositions = DasUBoot.model.xBombCollapseMatrix.getAllCoordinatesWhere((value:number) => {
            return value > 0;
        });

        //Updating all reelPositions
        for (let pos of fallDownPositions) {
            DasUBoot.reelView.reels[pos.column].collapseAvalancheSymbol(pos.row, DasUBoot.model.xBombCollapseMatrix.matrix[pos.column][pos.row]);
        }
        const fallDownSymbolAnimations = [];
        for (let i:number = fallDownPositions.length - 1; i >= 0; i--) {
            const pos = fallDownPositions[i];
            const symbolData = DasUBoot.reelView.getVisibleSymbolAt(pos.column, pos.row);
            const symbol = <DasUBootSymbol>symbolData.positionable;

            let offset = symbol.getXWayYOffset();

            if (resizedSymbol.indexOf(symbolData) >= 0){
               offset = 0;
            }


            const tl:gsap.core.Timeline = gsap.timeline();
            tl.add(symbol.getStateAnimation(DasUBootSymbolStates.COLLAPSE));
            tl.add(gsap.to(symbol.position, {
                duration:0.4,
                y:symbolData.reelPosition * DasUBootStatics.symbolHeight * symbol.getAvalancheScale() - offset,
                ease:DasUBootStatics.avalancheBounceEase
            }));
            tl.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.avalancheLandingSounds.next()), "<0.16");
            fallDownSymbolAnimations.push(tl);
        }

        return GSAPUtils.createTimelineFromArray(fallDownSymbolAnimations, "<0.08");

    }

    private getResizeAnimation():{anim:gsap.core.Timeline, symbols:SimpleReelModelSymbolData[]} {
        const tl:gsap.core.Timeline = gsap.timeline();
        const symbols:SimpleReelModelSymbolData[] = [];
        for (let data of DasUBoot.model.clientData.xWays) {
            //console.log(DasUBoot.model.xBombCollapseMatrix.toString());

            const shouldResetSize = DasUBoot.model.xBombCollapseMatrix.matrix[data.reel][data.position] < 0;

            if (shouldResetSize) {
                const symbolsLeftPositions = DasUBoot.model.xBombCollapseMatrix.getAllCoordinatesWhere((value:number, column?:number, row?:number) => {
                    return value >= 0 && column == data.reel;
                });

                for (let pos of symbolsLeftPositions) {
                    const symbolData = DasUBoot.reelView.getVisibleSymbolAt(pos.column, pos.row);
                    symbols.push(symbolData);
                    const symbol = <DasUBootSymbol>symbolData.positionable;
                    tl.add(symbol.resize(1, 0, 0.5), 0);
                }
            }
        }

        return {anim:tl, symbols:symbols};
    }


    shouldRun():boolean {
        return DasUBoot.model.gameData.nextMode == DasUBootGameMode.AVALANCHE;
    }
}